import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, defaultIfEmpty, exhaustMap, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { normalizeDate } from 'src/app/calls-dashboard/utils/date.utils';
import { ApiResponse } from 'src/app/models/api-response.model';
import { ClinicalTime, ContactStatus } from 'src/app/models/clinical.model';
import { environment } from 'src/environments/environment';
import { LastPatientCallService } from './last-patient-call.service';

/** A call shorter than this many seconds, ringing included, triggers the warning. Strictly under. */
export const SHORT_CALL_THRESHOLD_SEC = 30;

/** How far back a call can be and still be linked to the entry being saved. */
export const CALL_LINK_WINDOW_MS = 2 * 60 * 60 * 1000;

/** While a linked call's length is not in yet, ask again this often. */
export const SHORT_CALL_POLL_INTERVAL_MS = 1500;

/**
 * Hard limit on waiting for the length. It sets the number of polls (6 with the interval above, well
 * inside the endpoint's 30 per minute limit) and also cuts off a request that hangs. If the last poll
 * still has no length, the entry is saved as 'pending' right away rather than idling to the limit.
 */
export const SHORT_CALL_POLL_TIMEOUT_MS = 9000;

/** How many of the nurse's most recent calls the after-reload fallback looks through. */
export const RECENT_CALLS_FALLBACK_LIMIT = 20;

/**
 * Whether the contact answer means "I spoke live with the patient". The only place that decides it,
 * so when the question becomes a yes/no, this maps "yes" and nothing else has to change.
 */
export const isSpokeAnswer = (status: ContactStatus | string | null | undefined): boolean => status === 'spoke';

export type ShortCallFields = Pick<ClinicalTime, 'call_sid' | 'call_duration_sec' | 'short_call_check'>;

export interface ShortCallCheckResult {
  /** What to store on the entry. `short_call_check` is 'warned' only when `isShort` is true. */
  fields: ShortCallFields;
  /** True when the linked call was under the threshold, so the clinician must be asked first. */
  isShort: boolean;
}

interface CallDuration {
  callSid: string;
  durationSec: number | null;
  ended: boolean;
}

interface OwnCallListItem {
  callSid: string;
  userId?: string;
  patientId?: string;
  createdAt?: unknown;
}

/**
 * Looks up how long the call behind a "spoke" time entry lasted.
 *
 * Never blocks a save: anything it cannot work out comes back as `no_call` or `pending`.
 */
@Injectable({ providedIn: 'root' })
export class ShortCallCheckService {
  private readonly endpoint = environment.welbyEndpoint;

  constructor(private http: HttpClient, private lastPatientCall: LastPatientCallService) {}

  check(patientId: string, nurseUid: string, now: number = Date.now()): Observable<ShortCallCheckResult> {
    return this.findCallSid(patientId, nurseUid, now).pipe(
      switchMap((callSid) => (callSid ? this.checkDuration(callSid) : of(this.result({ short_call_check: 'no_call' })))),
      catchError(() => of(this.result({ short_call_check: 'no_call' })))
    );
  }

  /** The call this tab saw end, or after a reload, the newest of the nurse's own calls to this patient. */
  private findCallSid(patientId: string, nurseUid: string, now: number): Observable<string | null> {
    if (!patientId || !nurseUid) {
      return of(null);
    }
    const inSession = this.lastPatientCall.getRecent(patientId, nurseUid, CALL_LINK_WINDOW_MS, now);
    if (inSession) {
      return of(inSession.callSid);
    }
    return this.findInOwnCalls(patientId, nurseUid, now);
  }

  /**
   * GET /v1/calls with no userId returns only the caller's own calls, newest first, without any
   * transcript or recording. The same list the calls dashboard already reads.
   */
  private findInOwnCalls(patientId: string, nurseUid: string, now: number): Observable<string | null> {
    const params = new HttpParams().set('page', '1').set('limit', String(RECENT_CALLS_FALLBACK_LIMIT));
    return this.http
      .get<ApiResponse<{ data: OwnCallListItem[] }>>(`${this.endpoint}/api/v1/calls`, { params })
      .pipe(
        map((res) => {
          const match = (res?.data?.data ?? []).find((call) => {
            if (!call?.callSid || call.patientId !== patientId) {
              return false;
            }
            if (call.userId && call.userId !== nurseUid) {
              return false;
            }
            const startedAt = normalizeDate(call.createdAt)?.getTime();
            return startedAt !== undefined && !Number.isNaN(startedAt) && now - startedAt <= CALL_LINK_WINDOW_MS;
          });
          return match?.callSid ?? null;
        }),
        catchError(() => of(null))
      );
  }

  /**
   * Asks for the call's length, and keeps asking every SHORT_CALL_POLL_INTERVAL_MS while it is not in
   * yet: a 404 (the call record is not written yet) or a null length. Gives up after
   * SHORT_CALL_POLL_TIMEOUT_MS, or on any other error, and reports 'pending'.
   */
  private checkDuration(callSid: string): Observable<ShortCallCheckResult> {
    const pending = this.result({ call_sid: callSid, call_duration_sec: null, short_call_check: 'pending' });
    const maxPolls = Math.ceil(SHORT_CALL_POLL_TIMEOUT_MS / SHORT_CALL_POLL_INTERVAL_MS);
    return timer(0, SHORT_CALL_POLL_INTERVAL_MS).pipe(
      take(maxPolls),
      // A slow response is not stacked on by the next tick; that tick is skipped instead.
      exhaustMap(() => this.fetchDurationSec(callSid)),
      filter((durationSec): durationSec is number => durationSec !== null),
      take(1),
      takeUntil(timer(SHORT_CALL_POLL_TIMEOUT_MS)),
      map((durationSec) => {
        const isShort = durationSec < SHORT_CALL_THRESHOLD_SEC;
        return this.result(
          { call_sid: callSid, call_duration_sec: durationSec, short_call_check: isShort ? 'warned' : 'ok' },
          isShort
        );
      }),
      defaultIfEmpty(pending),
      catchError(() => of(pending))
    );
  }

  /** One request. Null means "not in yet, ask again"; any error other than a 404 ends the wait. */
  private fetchDurationSec(callSid: string): Observable<number | null> {
    return this.http
      .get<ApiResponse<CallDuration>>(`${this.endpoint}/api/v1/calls/${encodeURIComponent(callSid)}/duration`)
      .pipe(
        map((res) => {
          const durationSec = res?.data?.durationSec;
          return typeof durationSec === 'number' && Number.isFinite(durationSec) ? durationSec : null;
        }),
        catchError((error) =>
          error instanceof HttpErrorResponse && error.status === 404 ? of(null) : throwError(() => error)
        )
      );
  }

  private result(fields: ShortCallFields, isShort = false): ShortCallCheckResult {
    return { fields, isShort };
  }
}
